/**
 * Auth JavaScript - Handles verification forms and resend functionality
 */

class AuthHandler {
    constructor() {
        this.currentInterval = null;
        this.resendProcessed = false;
        this.turnstileForms = new WeakMap();
        this.csrfBootstrapPromise = null;
        
        this.init();
    }
    
    init() {
        // Auto-focus and initialize verification forms
        this.initializeVerificationForm();
        this.initializeAuthFormInputs();

        // Set up HTMX event listeners
        this.setupHTMXListeners();

        // Initialize Turnstile on page load (explicit rendering mode)
        this.initializeTurnstile();
        this.refreshCSRFToken();
        this.primeCsrfCookie();
    }
    
    initializeVerificationForm() {
        // iOS restores pages from the back-forward cache with DOM state
        // intact — clear the single-flight guard so the form works again.
        if (!window.pcOtpPageshowBound) {
            window.pcOtpPageshowBound = true;
            window.addEventListener('pageshow', (event) => {
                if (!event.persisted) return;
                document.querySelectorAll('form[data-auth-form="true"]').forEach((form) => {
                    delete form.dataset.otpSubmitting;
                    delete form.dataset.otpAutoSubmitted;
                    const submitBtn = form.querySelector('button[type="submit"]');
                    if (submitBtn) {
                        submitBtn.disabled = false;
                        submitBtn.classList.remove('opacity-60', 'cursor-not-allowed');
                    }
                });
            });
        }

        const codeInput = document.getElementById('code');
        if (codeInput) {
            codeInput.focus();
            this.initializeVerificationCodeInput(codeInput);

            // Disable resend button for initial 60s period (rate limiting still active on backend)
            const resendBtn = document.getElementById('resend-btn');
            if (resendBtn) {
                this.disableResendButton(60);
            }
        }
    }

    initializeVerificationCodeInput(codeInput) {
        if (!codeInput || codeInput.dataset.verifyCodeBound === 'true') return;

        codeInput.dataset.verifyCodeBound = 'true';

        const form = codeInput.closest('form[data-auth-form="true"]');

        // Single-flight guard: the OTP is single-use server-side, so a manual
        // "Verify" tap racing the auto-submit below must never produce two
        // POSTs (the duplicate used to consume the code and bounce the user
        // to "session expired" even though verification succeeded).
        if (form && form.dataset.otpSubmitGuardBound !== 'true') {
            form.dataset.otpSubmitGuardBound = 'true';
            form.addEventListener('submit', (event) => {
                if (form.dataset.otpSubmitting === 'true') {
                    event.preventDefault();
                    return;
                }
                form.dataset.otpSubmitting = 'true';
                const submitBtn = form.querySelector('button[type="submit"]');
                if (submitBtn) {
                    submitBtn.disabled = true;
                    submitBtn.classList.add('opacity-60', 'cursor-not-allowed');
                }
            });
        }

        codeInput.addEventListener('input', () => {
            const normalized = this.normalizeVerificationCode(codeInput.value);
            if (codeInput.value !== normalized) {
                codeInput.value = normalized;
            }

            this.clearVerifyCodeError(codeInput);

            if (!form) return;

            if (normalized.length < 6) {
                delete form.dataset.otpAutoSubmitted;
                return;
            }

            if (form.dataset.otpAutoSubmitted === 'true') return;
            form.dataset.otpAutoSubmitted = 'true';

            setTimeout(() => {
                if (form.dataset.otpSubmitting === 'true') return; // user already hit Verify
                if (typeof form.requestSubmit === 'function') {
                    form.requestSubmit();
                } else {
                    form.submit();
                }
            }, 80);
        });
    }

    normalizeVerificationCode(value) {
        return (value || '').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6);
    }

    clearVerifyCodeError(codeInput) {
        const form = codeInput.closest('form[data-auth-form="true"]');
        if (!form) return;

        form.querySelectorAll('[data-verify-code-error]').forEach((error) => error.remove());
        codeInput.classList.remove('border-red-300');
        codeInput.classList.add('border-gray-200');
    }

    initializeAuthFormInputs() {
        document.querySelectorAll('form[data-auth-form="true"] input[type="email"][name="email"]').forEach((input) => {
            if (input.dataset.authEmailInputBound === 'true') return;

            input.dataset.authEmailInputBound = 'true';
            input.addEventListener('input', () => {
                input.defaultValue = input.value;
                input.setAttribute('value', input.value);

                const form = input.closest('form[data-auth-form="true"]');
                if (!form) return;

                form.querySelectorAll('[data-auth-email-error]').forEach((error) => error.remove());
                input.classList.remove('border-red-300');
                input.classList.add('border-gray-200');
            });
        });
    }

    refreshCSRFToken() {
        const csrfCookie = this.getCookie('csrftoken');
        if (csrfCookie) {
            document.querySelectorAll('input[name="csrfmiddlewaretoken"]').forEach(input => {
                if (input.value !== csrfCookie) input.value = csrfCookie;
            });
        }
    }

    primeCsrfCookie() {
        const authForm = document.querySelector('form[data-auth-form="true"]');
        if (authForm && !this.getCookie('csrftoken')) {
            this.ensureCsrfCookie().then(() => this.refreshCSRFToken()).catch(() => {});
        }
    }

    getCookie(name) {
        let cookieValue = null;
        if (document.cookie && document.cookie !== '') {
            const cookies = document.cookie.split(';');
            for (let i = 0; i < cookies.length; i++) {
                const cookie = cookies[i].trim();
                if (cookie.substring(0, name.length + 1) === (name + '=')) {
                    cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
                    break;
                }
            }
        }
        return cookieValue;
    }

    ensureCsrfCookie() {
        if (this.getCookie('csrftoken')) {
            return Promise.resolve(true);
        }

        if (this.csrfBootstrapPromise) {
            return this.csrfBootstrapPromise;
        }

        this.csrfBootstrapPromise = fetch('/accounts/csrf/', {
            method: 'GET',
            credentials: 'same-origin',
            cache: 'no-store'
        })
            .then((response) => response.ok)
            .catch(() => false)
            .then((ok) => {
                this.refreshCSRFToken();
                return ok && !!this.getCookie('csrftoken');
            })
            .finally(() => {
                this.csrfBootstrapPromise = null;
            });

        return this.csrfBootstrapPromise;
    }
    
    initializeTurnstile(retryCount = 0) {
        const turnstileElements = document.querySelectorAll('.cf-turnstile:not([data-turnstile-rendered="true"])');
        if (!turnstileElements.length) return;

        // If Turnstile script not loaded yet, retry after delay (max 5 retries)
        if (typeof turnstile === 'undefined') {
            if (retryCount < 5) {
                setTimeout(() => this.initializeTurnstile(retryCount + 1), 200);
            }
            return;
        }

        turnstileElements.forEach((turnstileElement) => {
            const siteKey = turnstileElement.getAttribute('data-sitekey');
            const form = turnstileElement.closest('form');

            if (!siteKey || !this.isAuthForm(form)) return;

            try {
                const widgetId = turnstile.render(turnstileElement, {
                    sitekey: siteKey,
                    size: turnstileElement.getAttribute('data-size') || 'normal',
                    callback: (token) => this.handleTurnstileSuccess(form, token),
                    'error-callback': () => this.handleTurnstileFailure(form),
                    'expired-callback': () => this.handleTurnstileExpired(form)
                });

                turnstileElement.setAttribute('data-widget-id', String(widgetId));
                turnstileElement.setAttribute('data-turnstile-rendered', 'true');
            } catch (error) {}
        });
    }

    isAuthForm(form) {
        return !!form && form.dataset.authForm === 'true';
    }

    getTurnstileState(form) {
        let state = this.turnstileForms.get(form);
        if (!state) {
            state = {
                pendingSubmit: false,
                timeoutId: null
            };
            this.turnstileForms.set(form, state);
        }
        return state;
    }

    clearTurnstileState(form) {
        const state = this.getTurnstileState(form);
        state.pendingSubmit = false;
        if (state.timeoutId) {
            clearTimeout(state.timeoutId);
            state.timeoutId = null;
        }
    }

    getTurnstileContainer(form) {
        return form ? form.querySelector('.cf-turnstile') : null;
    }

    hasTurnstileToken(form) {
        const tokenInput = form ? form.querySelector('input[name="cf-turnstile-response"]') : null;
        return !!(tokenInput && tokenInput.value);
    }

    getSubmitButton(form) {
        return form ? form.querySelector('.login-btn, .signup-btn') : null;
    }

    showAuthFormError(form, message) {
        if (!form) return;

        let errorBox = form.querySelector('.auth-form-error');
        if (!errorBox) {
            errorBox = document.createElement('div');
            errorBox.className = 'auth-form-error bg-red-50 border border-red-100 rounded-xl p-3';
            form.prepend(errorBox);
        }

        errorBox.innerHTML = `<p class="text-red-600 text-sm">${message}</p>`;
    }

    setButtonLoading(form) {
        const btn = this.getSubmitButton(form);
        if (!btn) return;

        btn.disabled = true;

        const text = btn.querySelector('.btn-text');
        const arrow = btn.querySelector('.btn-arrow');
        const spinner = btn.querySelector('.btn-spinner');

        if (text) text.textContent = 'Verifying';
        if (arrow) arrow.classList.add('hidden');
        if (spinner) spinner.classList.remove('hidden');
    }

    resetButton(form) {
        const btn = this.getSubmitButton(form);
        if (!btn) return;

        btn.disabled = false;

        const text = btn.querySelector('.btn-text');
        const arrow = btn.querySelector('.btn-arrow');
        const spinner = btn.querySelector('.btn-spinner');

        if (text) text.textContent = 'Continue';
        if (arrow) arrow.classList.remove('hidden');
        if (spinner) spinner.classList.add('hidden');
    }

    executeTurnstile(form) {
        const container = this.getTurnstileContainer(form);
        if (!container || typeof turnstile === 'undefined') return false;

        const widgetId = container.getAttribute('data-widget-id');

        try {
            if (widgetId) {
                turnstile.execute(widgetId);
            } else {
                turnstile.execute(container);
            }
            return true;
        } catch (error) {
            try {
                if (widgetId) {
                    turnstile.reset(widgetId);
                    turnstile.execute(widgetId);
                } else {
                    turnstile.reset(container);
                    turnstile.execute(container);
                }
                return true;
            } catch (retryError) {
                return false;
            }
        }
    }

    handleTurnstileSuccess(form, token) {
        if (!form || !token) return;

        const state = this.getTurnstileState(form);
        if (!state.pendingSubmit) return;

        let tokenInput = form.querySelector('input[name="cf-turnstile-response"]');
        if (!tokenInput) {
            tokenInput = document.createElement('input');
            tokenInput.type = 'hidden';
            tokenInput.name = 'cf-turnstile-response';
            form.appendChild(tokenInput);
        }
        tokenInput.value = token;
        form.dataset.skipTurnstileCheck = 'true';

        this.clearTurnstileState(form);
        htmx.trigger(form, 'submit');
    }

    handleTurnstileFailure(form) {
        if (!this.isAuthForm(form)) return;
        this.clearTurnstileState(form);
        this.resetButton(form);
    }

    handleTurnstileExpired(form) {
        if (!this.isAuthForm(form)) return;
        this.clearTurnstileState(form);
        this.resetButton(form);
    }
    
    setupHTMXListeners() {
        // Configure HTMX to include CSRF token in all requests
        document.addEventListener('htmx:configRequest', (event) => {
            const csrfToken = this.getCookie('csrftoken');
            if (csrfToken) {
                event.detail.headers['X-CSRFToken'] = csrfToken;
            }
        });

        // Handle both successful and error responses
        const handleResendEvent = (event) => {
            const xhr = event.detail.xhr;

            if (xhr.responseURL && xhr.responseURL.includes('resend-verification-code')) {
                if (this.resendProcessed) return;
                this.resendProcessed = true;
                setTimeout(() => { this.resendProcessed = false; }, 1000);

                this.handleResendResponse(xhr);
            }
        };
        
        // Listen to both success and error events
        document.addEventListener('htmx:afterRequest', handleResendEvent);
        document.addEventListener('htmx:responseError', handleResendEvent);

        document.addEventListener('htmx:beforeRequest', (event) => {
            const resendButton = event.target;
            if (resendButton && resendButton.id === 'resend-btn' && !this.getCookie('csrftoken')) {
                if (resendButton.dataset.skipCsrfBootstrap === 'true') {
                    delete resendButton.dataset.skipCsrfBootstrap;
                    return;
                }

                event.preventDefault();
                resendButton.disabled = true;
                resendButton.classList.add('opacity-50');

                this.ensureCsrfCookie().then((success) => {
                    if (!success) {
                        this.showErrorOnButton('Refresh and try again');
                        return;
                    }

                    this.refreshCSRFToken();
                    resendButton.dataset.skipCsrfBootstrap = 'true';
                    htmx.trigger(resendButton, 'click');
                }).catch(() => {
                    this.showErrorOnButton('Refresh and try again');
                });
                return;
            }

            const form = event.target;
            if (!this.isAuthForm(form)) return;

            if (!this.getCookie('csrftoken')) {
                if (form.dataset.skipCsrfBootstrap === 'true') {
                    delete form.dataset.skipCsrfBootstrap;
                } else {
                    event.preventDefault();
                    this.setButtonLoading(form);

                    this.ensureCsrfCookie().then((success) => {
                        if (!success) {
                            this.resetButton(form);
                            this.showAuthFormError(
                                form,
                                'Secure form setup failed. Refresh and try again. If you opened this in Instagram, use Safari or Chrome.'
                            );
                            return;
                        }

                        this.refreshCSRFToken();
                        form.dataset.skipCsrfBootstrap = 'true';
                        htmx.trigger(form, 'submit');
                    }).catch(() => {
                        this.resetButton(form);
                        this.showAuthFormError(
                            form,
                            'Secure form setup failed. Refresh and try again. If you opened this in Instagram, use Safari or Chrome.'
                        );
                    });
                    return;
                }
            }

            if (form.dataset.skipTurnstileCheck === 'true') {
                delete form.dataset.skipTurnstileCheck;
                this.setButtonLoading(form);
                return;
            }

            if (!this.getTurnstileContainer(form) || this.hasTurnstileToken(form)) {
                this.setButtonLoading(form);
                return;
            }

            event.preventDefault();
            this.setButtonLoading(form);

            const state = this.getTurnstileState(form);
            state.pendingSubmit = true;

            if (state.timeoutId) {
                clearTimeout(state.timeoutId);
            }

            // Generous window: interactive Turnstile challenges (VPNs, privacy
            // browsers, flaky mobile networks) can take well over 10 seconds.
            state.timeoutId = setTimeout(() => {
                if (!state.pendingSubmit) return;
                this.clearTurnstileState(form);
                this.resetButton(form);
                this.showAuthFormError(form, 'Security verification timed out. Please try again.');
            }, 30000);

            if (!this.executeTurnstile(form)) {
                this.clearTurnstileState(form);
                this.resetButton(form);
                this.showAuthFormError(form, 'Security verification is still loading. Please wait a moment and try again.');
            }
        });

        document.addEventListener('htmx:afterRequest', (event) => {
            const form = event.target;
            if (!this.isAuthForm(form)) return;

            this.clearTurnstileState(form);
            this.resetButton(form);
        });

        // Re-initialize after HTMX swaps
        document.addEventListener('htmx:afterSwap', (event) => {
            const targetId = event.target.id;

            // Handle verification form containers and auth form containers
            if (targetId === 'verify-form-container' ||
                targetId === 'login-form-container' ||
                targetId === 'signup-form-container' ||
                targetId === 'hero-content' ||
                targetId === 'auth-content') {

                setTimeout(() => {
                    this.initializeVerificationForm();
                    this.initializeAuthFormInputs();
                    this.initializeTurnstile();
                    this.refreshCSRFToken();
                    this.primeCsrfCookie();
                }, 50);
            }
        });
    }
    
    handleResendResponse(xhr) {
        if (xhr.status === 200) {
            try {
                const response = JSON.parse(xhr.response);
                const resendBtn = document.getElementById('resend-btn');
                const messageDiv = document.getElementById('resend-message');

                if (response.success) {
                    // Success - show "New code sent!" and start 60s countdown
                    if (resendBtn) {
                        resendBtn.classList.remove('text-blue-600', 'hover:text-blue-700');
                        resendBtn.classList.add('text-emerald-600');

                        const spanElement = resendBtn.querySelector('span');
                        if (spanElement) {
                            spanElement.textContent = 'New code sent!';
                        }

                        // Start 60 second countdown
                        setTimeout(() => {
                            resendBtn.classList.remove('text-emerald-600');
                            this.disableResendButton(60);
                        }, 1500);
                    }
                } else {
                    // Error - show message
                    if (response.message && messageDiv) {
                        messageDiv.innerHTML = `<span class="text-red-600">${response.message}</span>`;
                        messageDiv.style.display = 'block';
                        setTimeout(() => { messageDiv.style.display = 'none'; }, 5000);
                    }

                    const waitSeconds = this.extractWaitSeconds(response.message);
                    if (waitSeconds && resendBtn && !response.redirect) {
                        this.disableResendButton(waitSeconds);
                        return;
                    }

                    // Re-enable button for non-redirect errors
                    if (resendBtn && !response.redirect) {
                        resendBtn.disabled = false;
                        resendBtn.classList.remove('opacity-50');
                        resendBtn.classList.add('text-blue-600', 'hover:text-blue-700');
                    }

                    // Redirect if needed
                    if (response.redirect) {
                        setTimeout(() => { window.location.href = response.redirect; }, 1500);
                    }
                }
            } catch (e) {}
        }
    }

    extractWaitSeconds(message) {
        const match = String(message || '').match(/wait\s+(\d+)\s+seconds?/i);
        return match ? Math.max(parseInt(match[1], 10), 1) : null;
    }
    
    showErrorOnButton(errorMessage) {
        const resendBtn = document.getElementById('resend-btn');
        if (!resendBtn) return;
        
        // Disable button and show error
        resendBtn.disabled = true;
        resendBtn.classList.add('opacity-50');
        resendBtn.classList.remove('text-blue-600', 'hover:text-blue-700');
        resendBtn.classList.add('text-red-500');
        
        const spanElement = resendBtn.querySelector('span');
        if (spanElement) {
            spanElement.textContent = errorMessage;
        }
        
        // Re-enable after 3 seconds
        setTimeout(() => {
            resendBtn.disabled = false;
            resendBtn.classList.remove('opacity-50', 'text-red-500');
            resendBtn.classList.add('text-blue-600', 'hover:text-blue-700');
            
            if (spanElement) {
                spanElement.textContent = 'Resend code';
            }
        }, 3000);
    }
    
    showPermanentError(errorMessage) {
        const resendBtn = document.getElementById('resend-btn');
        const messageDiv = document.getElementById('resend-message');
        
        if (!resendBtn) return;
        
        // Clear any existing intervals
        if (this.currentInterval) {
            clearInterval(this.currentInterval);
            this.currentInterval = null;
        }
        
        // Permanently disable button and show error
        resendBtn.disabled = true;
        resendBtn.classList.add('opacity-50');
        resendBtn.classList.remove('text-blue-600', 'hover:text-blue-700');
        resendBtn.classList.add('text-red-500');
        
        const spanElement = resendBtn.querySelector('span');
        if (spanElement) {
            spanElement.textContent = errorMessage;
        }
        
        // Also show message in the message div
        if (messageDiv) {
            messageDiv.innerHTML = `<span class="text-red-600">${errorMessage}</span>`;
            messageDiv.style.display = 'block';
        }
        
    }
    
    disableResendButton(seconds) {
        const resendBtn = document.getElementById('resend-btn');

        if (!resendBtn) return;

        // Clear any existing interval first
        if (this.currentInterval) {
            clearInterval(this.currentInterval);
            this.currentInterval = null;
        }

        // Disable button using disabled attribute and Tailwind classes
        resendBtn.disabled = true;
        resendBtn.classList.add('opacity-50');
        resendBtn.classList.remove('text-blue-600', 'hover:text-blue-700');

        // Start countdown timer
        let remaining = seconds;
        const originalText = 'Resend code';

        // Show initial countdown
        const spanElement = resendBtn.querySelector('span');
        if (spanElement) {
            spanElement.textContent = `Resend code in ${remaining}s`;
        }

        this.currentInterval = setInterval(() => {
            remaining--;
            const spanElement = resendBtn.querySelector('span');
            if (spanElement) {
                spanElement.textContent = remaining > 0 ? `Resend code in ${remaining}s` : originalText;
            }

            if (remaining <= 0) {
                clearInterval(this.currentInterval);
                this.currentInterval = null;

                // Re-enable button
                resendBtn.disabled = false;
                resendBtn.classList.remove('opacity-50');
                resendBtn.classList.add('text-blue-600', 'hover:text-blue-700');
            }
        }, 1000);
    }
    
}

// Initialize when DOM is ready
function initAuth() {
    if (typeof window.authHandler === 'undefined') {
        window.authHandler = new AuthHandler();
    }
}

// Run initialization
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAuth);
} else {
    initAuth();
}

// Also run immediately for HTMX swaps
setTimeout(initAuth, 50);
